import React from "react"
import styled from "@emotion/styled"
import { Outlet, To, useLocation, useMatch, useNavigate } from "react-router"
import {
  Medium12Paragraph,
  Medium14,
  Medium20Header2,
  Regular14UI,
} from "@myvp/shared/src/typography"
import { InternalNavLink } from "@myvp/shared/src/components/buttons/button-utils"
import studyRouteNames from "src/pages/study/study-route-names"
import { useRouteParams } from "@myvp/shared/src/hooks/use-route-params"
import { useIntl } from "react-intl"
import { useSponsorStudy } from "src/pages/study/use-sponsor-study"
import Home from "@myvp/shared/src/icons/home"
import TextSearch from "@myvp/shared/src/icons/text-search"
import InfoBalloon from "@myvp/shared/src/icons/info-balloon"
import PdfDownload from "@myvp/shared/src/icons/pdf-download"
import ChartTrendingUp from "@myvp/shared/src/icons/chart-trending-up"
import Badge from "@myvp/shared/src/components/badge"
import EcoaLogo from "@myvp/shared/src/components/ecoa-logo"
import VaultStudyLink from "src/components/vault-study-link"
import { sponsorsHelpLink } from "src/constants"
import Help from "@myvp/shared/src/icons/help"
import { useStudyHomeLoadTimestamp } from "src/pages/study/use-study-home-load-timestamp"
import { css, type Theme } from "@emotion/react"
import breakpoints from "@myvp/shared/src/styles/breakpoints"
import IconButton from "@myvp/shared/src/components/buttons/icon-button"
import Chevron from "@myvp/shared/src/icons/chevron"
import useStorageState from "@myvp/shared/src/hooks/use-storage-state"
import { isTouchDevice } from "@myvp/shared/src/functions/is-touch-device"
import Tooltip from "@myvp/shared/src/components/tooltip/tooltip"
import { DO_NOT_TRANSLATE_CONSTANTS } from "@myvp/shared/src/constants"
import ActionListRoot, {
  useActionListRoot,
} from "@myvp/shared/src/components/action-list/action-list-root"
import ActionList from "@myvp/shared/src/components/action-list/action-list"
import useMergeRefs from "@myvp/shared/src/hooks/use-merge-refs"
import { COMPLIANCE_DIMENSION_KEYS } from "src/pages/study/compliance/compliance.model"
import { logger } from "@myvp/shared/src/beacons/beacons.model"

const transitionTime = 0.2
const transitionCurve = "cubic-bezier(1, 0.4, 0.4, 1)"
export const mediumTabletWidth = 820

const collapseTransition = (isNavBarExpanded: boolean) => css`
  transition:
    opacity ${transitionTime}s ${transitionCurve},
    visibility ${transitionTime}s ${transitionCurve};
  opacity: ${isNavBarExpanded ? 1 : 0};
  visibility: ${isNavBarExpanded ? "visible" : "hidden"};
  white-space: nowrap;
  overflow: hidden;
`

const accordionTransition = (isOpen: boolean) => css`
  display: grid;
  grid-template-rows: ${isOpen ? "1fr" : "0fr"};
  opacity: ${isOpen ? 1 : 0};
  visibility: ${isOpen ? "visible" : "hidden"};
  transition:
    grid-template-rows ${transitionTime}s ${transitionCurve},
    opacity ${transitionTime}s ${transitionCurve},
    visibility ${transitionTime}s ${transitionCurve};
  overflow: hidden;
`

const Container = styled.div<{ isNavBarExpanded: boolean }>`
  height: 100%;
  background-color: ${(props) =>
    props.theme.palette.background.neutral.default};
  display: grid;
  grid-template-columns:
    [nav-start] ${(props) => (props.isNavBarExpanded ? "240px" : "56px")}
    [nav-end content-start] minmax(0px, 1fr)
    [content-end];

  transition: grid-template-columns ${transitionTime}s ${transitionCurve};

  @media (max-width: ${mediumTabletWidth}px) {
    grid-template-columns:
      [nav-start] 56px
      [nav-end content-start] minmax(0px, 1fr)
      [content-end];
  }
`

const Content = styled.main`
  grid-column: content;
  width: 100%;
  height: 100%;
  overflow: auto;
  display: grid;
  grid-template-rows: minmax(0px, 1fr);
  min-width: 0;
`

interface TabItemProps {
  to: To
  label: string
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  className?: string
  isNavBarExpanded: boolean
  onClick?: (e: React.MouseEvent) => void
  end?: boolean
  ref?: React.Ref<HTMLAnchorElement>
  isTooltipEnabled: boolean
  tabIndex?: number
  testId?: string
}

const interactionStyles = (theme: Theme) => css`
  cursor: pointer;
  outline: none;
  text-decoration: none;

  --bg-color: transparent;
  --border-color: transparent;

  &:focus-visible {
    --border-color: ${theme.palette.border.focus.default};
    --bg-color: ${theme.palette.surface.vault.hover};
  }

  &:hover {
    --bg-color: ${theme.palette.surface.vault.hover};
  }

  &:is([aria-selected="true"], [aria-current="page"], .active) {
    --bg-color: ${theme.palette.surface.vault.selected};
  }

  &:active {
    --bg-color: ${theme.palette.surface.vault.pressed};
  }
`

const TabItem = (props: TabItemProps) => {
  return (
    <Tooltip
      key={String(props.isTooltipEnabled)}
      placement="right"
      active={props.isTooltipEnabled}
      content={<Medium12Paragraph>{props.label}</Medium12Paragraph>}
    >
      <InternalNavLink
        ref={props.ref}
        aria-label={props.label}
        className={props.className}
        to={props.to}
        onClick={props.onClick}
        end={props.end}
        tabIndex={props.tabIndex}
        data-testid={props.testId}
        css={(theme) => css`
          padding: 8px;
          position: relative;
          ${interactionStyles(theme)};
        `}
      >
        {({ isActive }) => {
          const Typography = isActive ? Medium14 : Regular14UI

          return (
            <div
              css={(theme) => css`
                display: flex;
                color: ${theme.palette.content.neutral.inverse};
                align-items: center;
                padding: 8px;
                gap: 4px;
                border-radius: 4px;
                background-color: var(--bg-color);
                box-shadow: inset 0 0 0 2px var(--border-color);
              `}
            >
              {props.startIcon}
              <div
                css={css`
                  flex-grow: 1;
                  ${collapseTransition(props.isNavBarExpanded)}
                `}
              >
                <Typography data-testid={`nav-label-${props.label}`}>
                  {props.label}
                </Typography>
              </div>
              {props.endIcon}
            </div>
          )
        }}
      </InternalNavLink>
    </Tooltip>
  )
}

const ComplianceTabItem = (props: TabItemProps) => {
  const actionListRoot = useActionListRoot()
  const ref = useMergeRefs([actionListRoot.anchorRef, props.ref])
  const { onClick: rootOnClick, ...referenceProps } =
    actionListRoot.getReferenceProps(props) as Record<string, any>

  return (
    <TabItem
      {...props}
      {...referenceProps}
      ref={ref}
      onClick={(e) => {
        props.onClick?.(e)
        if (!props.isNavBarExpanded) {
          rootOnClick?.(e)
        }
      }}
    />
  )
}

const Study = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const isIndexRoute = Boolean(useMatch(studyRouteNames.study.route))
  const routeParams = useRouteParams(studyRouteNames.study)
  const { data } = useSponsorStudy()

  const [hasSentBeacon, setHasSentBeacon] = useStorageState(
    false,
    "has-sent-beacon"
  )
  React.useEffect(() => {
    if (!hasSentBeacon) {
      void logger("ACTIVITY_SPONSOR_LOAD_STUDY_HOME")
      setHasSentBeacon(true)
    }
  }, [hasSentBeacon, setHasSentBeacon])

  const dataChangesWaitingApprovalCount =
    data?.dataChangesWaitingApprovalCount ?? 0

  const hasMultipleCountries = (data?.siteStudiesByCountry.length ?? 0) > 1

  const [isNavBarExpanded, setIsNavBarExpanded] = useStorageState(
    window.innerWidth > mediumTabletWidth,
    "nav-bar-expanded"
  )
  const [isNavBarHovered, setIsNavBarHovered] = React.useState(false)
  const { search } = useLocation()
  const [isComplianceOpen, setIsComplianceOpen] = useStorageState(
    true,
    "compliance-expanded"
  )
  const [isActionListOpen, setIsActionListOpen] = React.useState(false)

  // initialize at the top level so that other components can read/subscribe to the initial load time
  useStudyHomeLoadTimestamp()

  const isCollapsedActionListOpen = !isNavBarExpanded && isActionListOpen

  const isTooltipEnabled =
    !isNavBarExpanded && !isTouchDevice() && !isCollapsedActionListOpen

  return (
    <Container isNavBarExpanded={isNavBarExpanded}>
      <div
        data-testid="navigation-bar"
        onMouseEnter={() => setIsNavBarHovered(true)}
        onMouseLeave={() => setIsNavBarHovered(false)}
        css={(theme) => css`
          grid-column: nav;
          background-color: ${theme.palette.surface.vault.default};
          display: flex;
          flex-direction: column;
          border-right: 1px solid ${theme.palette.border.neutral.default};
          width: ${isNavBarExpanded ? "240px" : "56px"};
          transition:
            width ${transitionTime}s ${transitionCurve},
            z-index 0s linear ${isNavBarExpanded ? "0s" : `${transitionTime}s`};
          z-index: ${isNavBarExpanded ? theme.zIndex.drawer - 1 : 1};

          @media (max-width: ${mediumTabletWidth}px) {
            box-shadow: ${isNavBarExpanded
              ? "0 12px 48px 0 rgba(0, 0, 0, 0.15)"
              : "none"};
          }
        `}
      >
        {/* VEEVA LOGO */}
        <div
          css={(theme) => css`
            display: flex;
            padding: 16px;
            align-items: center;
            border-bottom: 1px solid ${theme.palette.surface.vault.pressed};
            position: relative;
          `}
        >
          <EcoaLogo
            css={css`
              h1 {
                ${collapseTransition(isNavBarExpanded)}
                ${breakpoints("md")} {
                  display: block;
                }
              }
            `}
          />
          <Medium20Header2
            as="h1"
            css={(theme) => css`
              font-family: var(--logo-fontFamily);
              color: ${theme.palette.content.neutral.inverse};
              ${collapseTransition(isNavBarExpanded)}
            `}
          >
            &nbsp;{formatMessage({ id: "sponsorStudyHome.studyHome" })}
          </Medium20Header2>
          {(isTouchDevice() || isNavBarHovered) &&
            !isCollapsedActionListOpen && (
              <div
                css={css`
                  position: absolute;
                  right: 0;
                  transform: translateX(50%);
                `}
              >
                <IconButton
                  title={
                    isNavBarExpanded
                      ? formatMessage({ id: "eproModule.collapse" })
                      : formatMessage({ id: "eproModule.expand" })
                  }
                  css={(theme) => css`
                    && {
                      border-radius: 32px;
                      ${interactionStyles(theme)}
                      --bg-color: ${theme.palette.surface.vault.selected};
                      background-color: var(--bg-color);
                      border: 2px solid var(--border-color);
                    }
                  `}
                  variant="tertiary"
                  height="small"
                  internalType="button"
                  icon={() => (
                    <Chevron
                      color="inverse"
                      variant={isNavBarExpanded ? "left" : "right"}
                    />
                  )}
                  onClick={() => setIsNavBarExpanded(!isNavBarExpanded)}
                />
              </div>
            )}
        </div>
        {/*  NAVIGATION TABS */}
        <div
          css={css`
            flex-grow: 1;
            display: flex;
            flex-direction: column;

            a {
              visibility: ${data ? "visible" : "hidden"};
            }
          `}
          role="navigation"
          aria-label={formatMessage({ id: "sponsorStudyNav.navLabel" })}
        >
          <TabItem
            isNavBarExpanded={isNavBarExpanded}
            startIcon={<Home size="medium" color="inverse" />}
            className={isIndexRoute ? "active" : undefined}
            isTooltipEnabled={isTooltipEnabled}
            to={{
              pathname: studyRouteNames.studyOverview(routeParams),
              search,
            }}
            label={formatMessage({ id: "sponsorStudyNav.overview" })}
          />
          <TabItem
            isNavBarExpanded={isNavBarExpanded}
            startIcon={<TextSearch size="medium" color="inverse" />}
            isTooltipEnabled={isTooltipEnabled}
            to={{
              pathname: studyRouteNames.sites(routeParams),
              search,
            }}
            label={formatMessage({ id: "sponsorStudyNav.sites" })}
            testId="top-level-sites"
          />
          <ActionListRoot
            open={isCollapsedActionListOpen}
            onOpenChange={setIsActionListOpen}
          >
            <ComplianceTabItem
              isNavBarExpanded={isNavBarExpanded}
              startIcon={<ChartTrendingUp size="medium" color="inverse" />}
              to={{
                pathname: studyRouteNames.compliance(routeParams),
                search,
              }}
              label={formatMessage({ id: "sponsorStudyNav.compliance" })}
              end={isNavBarExpanded}
              isTooltipEnabled={isTooltipEnabled}
              endIcon={
                isNavBarExpanded && (
                  <Chevron
                    color="inverse"
                    variant={isComplianceOpen ? "up" : "down"}
                    size="small"
                  />
                )
              }
              onClick={(e) => {
                e.preventDefault()
                if (isNavBarExpanded) {
                  setIsComplianceOpen(!isComplianceOpen)
                }
              }}
            />
            {!isNavBarExpanded && (
              <ActionList
                applySize={() => ({
                  width: "185px",
                  maxWidth: "185px",
                })}
                placement="right-start"
                actions={[
                  {
                    type: "item",
                    label: formatMessage({ id: "sponsorStudyNav.study" }),
                    onClick: () => {
                      setIsComplianceOpen(true)
                      navigate({
                        pathname: studyRouteNames.complianceDimension({
                          ...routeParams,
                          complianceDimension: COMPLIANCE_DIMENSION_KEYS.study,
                        }),
                        search,
                      })
                    },
                  },
                  ...(hasMultipleCountries
                    ? [
                        {
                          type: "item" as const,
                          label: formatMessage({
                            id: "sponsorStudyNav.countries",
                          }),
                          onClick: () => {
                            setIsComplianceOpen(true)
                            navigate({
                              pathname: studyRouteNames.complianceDimension({
                                ...routeParams,
                                complianceDimension:
                                  COMPLIANCE_DIMENSION_KEYS.countries,
                              }),
                              search,
                            })
                          },
                        },
                      ]
                    : []),
                  {
                    type: "item",
                    label: formatMessage({ id: "sponsorStudyNav.sites" }),
                    onClick: () => {
                      setIsComplianceOpen(true)
                      navigate({
                        pathname: studyRouteNames.complianceDimension({
                          ...routeParams,
                          complianceDimension: COMPLIANCE_DIMENSION_KEYS.sites,
                        }),
                        search,
                      })
                    },
                  },
                  {
                    type: "item",
                    label: formatMessage({
                      id: "sponsorStudyNav.participants",
                    }),
                    onClick: () => {
                      setIsComplianceOpen(true)
                      navigate({
                        pathname: studyRouteNames.complianceDimension({
                          ...routeParams,
                          complianceDimension:
                            COMPLIANCE_DIMENSION_KEYS.participants,
                        }),
                        search,
                      })
                    },
                  },
                ]}
              />
            )}
          </ActionListRoot>
          <div css={accordionTransition(isComplianceOpen && isNavBarExpanded)}>
            <div
              css={(theme) => css`
                min-height: 0;
                display: flex;
                flex-direction: column;
                margin-inline-start: 28px;
                border-left: 1px solid ${theme.palette.surface.vault.pressed};
              `}
            >
              <TabItem
                isTooltipEnabled={false}
                isNavBarExpanded={isNavBarExpanded}
                tabIndex={isComplianceOpen && isNavBarExpanded ? 0 : -1}
                to={{
                  pathname: studyRouteNames.complianceDimension({
                    ...routeParams,
                    complianceDimension: COMPLIANCE_DIMENSION_KEYS.study,
                  }),
                  search,
                }}
                label={formatMessage({ id: "sponsorStudyNav.study" })}
              />
              {hasMultipleCountries && (
                <TabItem
                  isTooltipEnabled={false}
                  isNavBarExpanded={isNavBarExpanded}
                  tabIndex={isComplianceOpen && isNavBarExpanded ? 0 : -1}
                  to={{
                    pathname: studyRouteNames.complianceDimension({
                      ...routeParams,
                      complianceDimension: COMPLIANCE_DIMENSION_KEYS.countries,
                    }),
                    search,
                  }}
                  label={formatMessage({ id: "sponsorStudyNav.countries" })}
                />
              )}
              <TabItem
                isTooltipEnabled={false}
                isNavBarExpanded={isNavBarExpanded}
                tabIndex={isComplianceOpen && isNavBarExpanded ? 0 : -1}
                to={{
                  pathname: studyRouteNames.complianceDimension({
                    ...routeParams,
                    complianceDimension: COMPLIANCE_DIMENSION_KEYS.sites,
                  }),
                  search,
                }}
                label={formatMessage({ id: "sponsorStudyNav.sites" })}
                testId="compliance-sites"
              />
              <TabItem
                isTooltipEnabled={false}
                isNavBarExpanded={isNavBarExpanded}
                tabIndex={isComplianceOpen && isNavBarExpanded ? 0 : -1}
                to={{
                  pathname: studyRouteNames.complianceDimension({
                    ...routeParams,
                    complianceDimension: COMPLIANCE_DIMENSION_KEYS.participants,
                  }),
                  search,
                }}
                label={formatMessage({
                  id: "sponsorStudyNav.participants",
                })}
              />
            </div>
          </div>

          <TabItem
            isNavBarExpanded={isNavBarExpanded}
            startIcon={<InfoBalloon size="medium" color="inverse" />}
            isTooltipEnabled={isTooltipEnabled}
            to={{
              pathname: studyRouteNames.dataChanges(routeParams),
              search,
            }}
            label={formatMessage({ id: "studyHome.dataChanges" })}
            endIcon={
              dataChangesWaitingApprovalCount > 0 ? (
                <Badge
                  css={css`
                    ${!isNavBarExpanded &&
                    css`
                      position: absolute;
                      top: 14px;
                      left: 50%;
                      min-width: 14px;
                      height: 12px;
                      min-height: 12px;
                      line-height: 12px;
                      font-size: 10px;
                      padding: 0 2px;
                    `}
                  `}
                  variant="primary"
                  char={
                    dataChangesWaitingApprovalCount > 99
                      ? "99+"
                      : dataChangesWaitingApprovalCount
                  }
                  data-testid="indicator"
                />
              ) : undefined
            }
          />
          <TabItem
            isNavBarExpanded={isNavBarExpanded}
            startIcon={<PdfDownload size="medium" color="inverse" />}
            isTooltipEnabled={isTooltipEnabled}
            to={{
              pathname: studyRouteNames.reports(routeParams),
              search,
            }}
            label={formatMessage({ id: "sponsorStudyNav.reports" })}
          />
        </div>
        {/* FOOTER */}
        <div
          css={(theme) => css`
            display: flex;
            flex-direction: ${isNavBarExpanded ? "row" : "column"};
            padding: 8px;
            border-top: 1px solid ${theme.palette.surface.vault.pressed};
            gap: ${isNavBarExpanded ? "0" : "8px"};

            a {
              ${interactionStyles(theme)};
              background-color: var(--bg-color);
              border-radius: 4px;
              box-shadow: inset 0 0 0 2px var(--border-color);
              overflow: hidden;

              span {
                margin-inline-start: 0;
                padding: 8px;
              }

              p {
                ${collapseTransition(isNavBarExpanded)}
              }
            }
          `}
        >
          <Tooltip
            placement="right"
            verticalOffset={8}
            active={isTooltipEnabled}
            content={
              <Medium12Paragraph>
                {formatMessage(
                  { id: "sponsorStudyNav.returnToVault" },
                  { vault: DO_NOT_TRANSLATE_CONSTANTS.vault }
                )}
              </Medium12Paragraph>
            }
          >
            <div
              css={css`
                display: flex;
              `}
            >
              <VaultStudyLink variant="white" />
            </div>
          </Tooltip>
          <Tooltip
            placement="right"
            verticalOffset={8}
            active={isTooltipEnabled}
            content={
              <Medium12Paragraph>
                {formatMessage({ id: "eproModule.help" })}
              </Medium12Paragraph>
            }
          >
            <a
              css={css`
                display: flex;
                padding: 8px;
                margin-inline-start: auto;
              `}
              color="inherit"
              target="_blank"
              rel="noreferrer"
              href={sponsorsHelpLink}
              title={formatMessage({ id: "eproModule.help" })}
            >
              <Help color="inverse" size="medium" />
            </a>
          </Tooltip>
        </div>
      </div>
      {/* CONTENT */}
      <Content>
        <Outlet />
      </Content>
    </Container>
  )
}

export default Study
